import type { ReactNode } from 'react';
import type { StepUpTier } from '@elutide/shared';
import { getAuthAdapter } from '@/lib/api';
import { Button } from './Primitives';

export interface StepUpGateProps {
  tier: StepUpTier;
  /** What the operator is about to do, shown when the gate has to explain itself. */
  action: string;
  children: ReactNode;
  onSatisfied?: (stepUpId: string) => void;
}

/**
 * Placeholder that delegates to A2.
 *
 * A2 owns the real step-up experience (passkey assertion, PIN for tier 2, the 5 minute
 * window). This shell component only asks the registered auth adapter for one and renders
 * its children once the adapter says the step-up is held. When A2's component lands it
 * replaces the body of this one; the props do not change, so no caller has to be edited.
 *
 * If no adapter is registered, which is the case in mock mode, the gate renders its children
 * and says so. It is never the control: the API refuses the request without a real step-up.
 */
export function StepUpGate({ tier, action, children, onSatisfied }: StepUpGateProps) {
  const adapter = getAuthAdapter();
  const held = adapter.currentStepUpId?.(tier) ?? null;

  if (held || !adapter.requestStepUp) {
    return <>{children}</>;
  }

  return (
    <div className="o-notice">
      <p>
        {action} needs a tier {tier} step-up
        {tier === 3 ? ', which is a fresh passkey assertion every time.' : '.'}
      </p>
      <Button
        variant="primary"
        onClick={() => {
          void adapter.requestStepUp?.(tier).then((id) => {
            if (id) onSatisfied?.(id);
          });
        }}
      >
        Verify identity
      </Button>
    </div>
  );
}
